import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
    FiChevronUp,
    FiChevronDown,
    FiInfo,
    FiX,
    FiPaperclip,
    FiFileText,
    FiFile,
    FiAlertCircle,
    FiMessageSquare,
} from "react-icons/fi";
import { useReviewerContext } from "../../context/ReviewerContext";
import { questionnairesApi, crosswalkApi } from "../../services/api";
import ManageAnswerDocumentsModal from "./ManageAnswerDocumentsModal";
import QuestionChat from "../../components/QuestionChat";

const confidenceLevels = ["Very Low", "Low", "Moderate", "High", "Very High"];

const QuestionCard = ({ question }) => {
    // ─── Context ───────────────────────────────────────────────────────────────
    const {
        activeQnr,
        proposal,
        sourceAnswer, // Get sourceAnswer from context
        toggleSelect,
        handleApprove,
        handleReject,
        handleUndo,
        updateQuestion,
        handleOptionChange,
        handleConfidenceChange,
        handleSaveAnswer,
        saveReviewerNotes,
        handleCloseUploadPanel,
        chatStatusMap,
        refreshChatStatuses,
    } = useReviewerContext();

    // ─── Route params ────────────────────────────────────────────────────────────
    const { assessmentId } = useParams();

    // ─── States ────────────────────────────────────────────────────────────────
    const [showDetails, setShowDetails] = useState(false);
    const [showChat, setShowChat] = useState(false);
    const [chatMaximized, setChatMaximized] = useState(false);
    const [detailTab, setDetailTab] = useState("Cross-Walk");
    const [uploadFile, setUploadFile] = useState(null);
    const [showDocsModal, setShowDocsModal] = useState(false);
    const [docCount, setDocCount] = useState(question?.refDocCount ?? null);

    // Keep the badge count in sync with the server-provided count once answers finish loading
    // (or after a reload). The modal still updates docCount directly on upload/delete; that count
    // sticks because refDocCount only changes on a fresh fetch.
    useEffect(() => {
        setDocCount(question?.refDocCount ?? null);
    }, [question?.refDocCount]);

    // ─── Derived Values ────────────────────────────────────────────────────────
    const q = question;
    // console.log('Rendering QuestionCard for question:', q);
    const isReviewed = q.reviewStatus !== null;
    const isApproved = q.reviewStatus === "approved";
    const isRejected = q.reviewStatus === "rejected";
    const hasPendingChanges =
        q.pendingAnswer !== null || q.pendingConfidence !== null;

    // Debug: Log proposal matching for this question
    console.log(
        `QUESTION ${q.id}: Looking for proposal with target_question_id=${q.id}`,
    );
    console.log(
        `QUESTION ${q.id}: Available proposals:`,
        proposal?.map((p) => p.target_question_id),
    );

    const rec = proposal?.find((r) => r.target_question_id === q.id);

    if (rec) {
        console.log(`QUESTION ${q.id}: Found matching proposal:`, rec);
    } else {
        console.log(`QUESTION ${q.id}: No matching proposal found`);
    }



    // Compute match type for this question (used in options and answer row)
    const sourceAns = sourceAnswer?.find(
        (sa) => sa.answer_id === rec?.source_answer_id,
    );
    const evidenceDocuments = rec?.evidence_documents || [];
    const linkedEvidenceDocuments = evidenceDocuments.filter((d) => d.blob_url);
    const snippetEvidenceDocuments = evidenceDocuments.filter((d) => d.snippet);
    const documentEnrichment =
        rec?.document_enrichment || rec?.documentEnrichment || null;
    let matchType = null;
    let matchedOptionIdx = -1;
    let matchPercent = null;

    // Questionnaire Intelligence matching: rely on the crosswalk proposal's
    // semantic option alignments instead of raw string comparison. Each
    // alignment maps a target option (by id) to the source answer with a
    // similarityScore (0–1). We take the primary suggestion (or the highest
    // scoring one) and treat anything below 50% as "No Match".
    const optionAlignments = rec?.option_alignments || [];
   
        const primaryAlignment =
            optionAlignments.find((a) => a.is_primary) ||
            [...optionAlignments].sort(
                (a, b) => (b.alignment_score || 0) - (a.alignment_score || 0),
            )[0];

        if (primaryAlignment) {
            matchPercent = Math.round(
                (primaryAlignment.alignment_score || 0) * 100,
            );
            const idx = q.answerOptions?.findIndex(
                (o) => o.id === primaryAlignment.target_option_id,
            );

            if (matchPercent >= 50 && idx >= 0) {
                matchType = "Match";
                matchedOptionIdx = idx;
            } else {
                matchType = "No Match";
            }
        }

    // Anything that is not a positive "Match" (i.e. below-50% alignment OR no
    // alignment data at all) is treated as "No Match" for the answer row.
    const isNoMatch = matchType !== "Match";

    const borderColor = isApproved
        ? "border-[ #0076b570]"
        : isRejected
          ? "border-[ #f4812e55]"
          : q.selected
            ? "border-[ #f4812e63]"
            : "border-[ #91919144]";

    function formatRationale(input) {
        let text = input ?? "";

        // remove "Details:"
        text = text.replace(/Details:/gi, "");

        // add newline after :
        text = text.replace(/:/g, ":\n");

        // bold from start → first :
        text = text.replace(/^([^:]+):/, "<b>$1:</b>");

        // bold from . → next :
        text = text.replace(/\.([^:]+):/g, ".<b>$1:</b>");

        return text;
    }

    const handleOpenDocument = async () => {
        if (!q.answerId || !activeQnr) return;
        try {
            const result = await questionnairesApi.getDocumentUrl(
                activeQnr,
                q.answerId,
            );
            window.open(result.url, "_blank", "noopener,noreferrer");
        } catch (err) {
            console.error("Failed to open document", err);
        }
    };

    // Download a crosswalk evidence document. The raw blob URL is not publicly accessible, so we
    // exchange it for a short-lived SAS URL (signed to force download, not inline render), then
    // trigger a direct download without opening a new tab.
    const openEvidenceDoc = async (blobUrl) => {
        try {
            const { url } = await crosswalkApi.getEvidenceDocumentUrl(blobUrl);
            const a = document.createElement("a");
            a.href = url;
            a.rel = "noopener";
            document.body.appendChild(a);
            a.click();
            a.remove();
        } catch (err) {
            console.error("Failed to download evidence document", err);
        }
    };

    const evidenceTitle = (doc) =>
        doc.doc_title || doc.blob_url?.split("/").pop() || "Document Evidence";
    const formatEvidenceScore = (score) => {
        const numericScore = Number(score);
        if (!Number.isFinite(numericScore)) return null;
        if (numericScore >= 0 && numericScore <= 1)
            return `${Math.round(numericScore * 100)}% match`;
        return `Score ${numericScore.toFixed(2)}`;
    };
    const formatSupportStatus = (status) =>
        (status || "document evidence")
            .replace(/_/g, " ")
            .replace(/\b\w/g, (letter) => letter.toUpperCase());

    // ─── View Query chat body (shared between slide-in panel & maximized modal) ──
    // The reviewer never starts a thread — the button only appears once the user
    // has raised a query (status 1 = open, 2 = resolved).
    const queryStatus = chatStatusMap?.[q.id];
    const hasQuery = queryStatus === 1 || queryStatus === 2;

    const closeChat = () => {
        setShowChat(false);
        setChatMaximized(false);
        refreshChatStatuses?.();
    };

    const chatBody = (
        <QuestionChat
            assessmentId={assessmentId ? parseInt(assessmentId, 10) : null}
            questionId={q.id}
            answerId={q.answerId ?? null}
            questionText={q.text}
            senderRole="reviewer"
            open={showChat}
            chatMaximized={chatMaximized}
            setChatMaximized={setChatMaximized}
            onClose={closeChat}
        />
    );

    // ─── Render ────────────────────────────────────────────────────────────────

    return (
        <div
            className={` border-2 ${borderColor} p-4 transition-all relative overflow-hidden`}
        >
            {/* Question header */}
            <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 flex-1">
                    <div className="flex items-start gap-2 mt-0.5">
                        <span className="text-xs font-semibold text-[#919191] flex items-center gap-1">
                            Question{" "}
                            {/* <FiInfo className="text-[#919191] text-xs" title="Question info" /> */}
                        </span>
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    {hasQuery && (
                        <button
                            onClick={() => setShowChat(true)}
                            className="text-xs font-semibold text-[#0077b5] hover:text-[#005a8f] flex items-center gap-1.5 flex-shrink-0 border border-[#0077b5] px-3 py-1.5 hover:bg-[#0077b5]/10 transition-colors"
                        >
                            <FiMessageSquare className="text-sm" />
                            View Query
                        </button>
                    )}
                    <button
                        onClick={() =>
                            updateQuestion(q.id, {
                                showExplanation: !q.showExplanation,
                            })
                        }
                        className="text-xs text-[#919191] hover:text-[#0077b5] flex items-center gap-1 flex-shrink-0"
                    >
                        {q.showExplanation
                            ? "Hide explanation"
                            : "Show explanation"}
                        {q.showExplanation ? (
                            <FiChevronUp className="text-xs" />
                        ) : (
                            <FiChevronDown className="text-xs" />
                        )}
                    </button>
                </div>
            </div>

            {/* ─── Side-by-side layout: Question + Upload panel ─── */}
            <div
                className={`flex flex-col ${q.showUploadPanel ? "md:flex-row md:items-start" : ""} gap-60 mt-2`}
            >
                {/* Left side: question text + options */}
                <div className="flex-shrink-0">
                    <div className="flex items-start gap-2">
                        {!isReviewed && (
                            <input
                                type="checkbox"
                                checked={q.selected}
                                onChange={() => toggleSelect(q.id)}
                                className="w-4 h-4 mt-0.5 accent-[#F47F2E] cursor-pointer flex-shrink-0"
                            />
                        )}
                        <p
                            className={`text-sm font-semibold ${q.selected ? "text-[#F47F2E]" : "text-[#0077b5]"}`}
                        >
                            {q.text}
                        </p>
                    </div>

                    <div className="mt-3 ml-6 space-y-1.5">
                        {q.options.map((opt, idx) => {

                            const isSelected =
                                q.pendingAnswer !== null
                                    ? q.pendingAnswer === idx
                                    : q.selectedOption === idx;
                            const isPending = q.pendingAnswer === idx;
                            const isMatchedOption =
                                matchedOptionIdx === idx &&
                                matchType &&
                                matchType !== "No Match";
                            const hasRealSelection =
                                q.pendingAnswer !== null ||
                                q.selectedOption !== null;

                            return (
                                <label
                                    key={idx}
                                    className="flex items-center gap-2 cursor-pointer text-sm text-[#53545a]"
                                >
                                    <input
                                        type="radio"
                                        name={`option-${q.id}`}
                                        checked={
                                            isSelected ||
                                            (!hasRealSelection &&
                                                isMatchedOption)
                                        }
                                        onChange={() =>
                                            handleOptionChange(q.id, idx)
                                        }
                                        className="accent-[#0077b5]"
                                        disabled={isReviewed}
                                    />
                                    {opt}
                                    {isPending && (
                                        <span className="text-[10px] text-[#F47F2E] font-medium ml-1">
                                            (pending)
                                        </span>
                                    )}
                                    {isMatchedOption && (
                                        <span className="text-[10px] text-green-600 font-medium ml-1">
                                            (
                                            {matchPercent != null
                                                ? `${matchPercent}% match by `
                                                : ""}
                                            Questionnaire intelligence)
                                        </span>
                                    )}
                                </label>
                            );
                        })}
                    </div>
                </div>

                {/* Upload Panel: sits right beside the question */}
                {/* 
         q.showUploadPanel && (
          <div className="w-full md:w-[260px] flex-shrink-0 bg-[#f7f8fa] border border-[#e0e2e6] rounded-xl p-1 sm:p-2 relative transition-all self-center">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-xs font-bold text-[#53545a]">
                Upload Supporting Document
              </h4>
              <button
                onClick={() => handleCloseUploadPanel(q.id)}
                className="p-1.5 rounded-lg hover:bg-[#919191]/10 text-[#919191] transition-colors"
                title="Close"
              >
                <FiX className="text-sm" />
              </button>
            </div>

            <label className="flex flex-col items-center justify-center border-2 border-dashed border-[#919191] rounded-xl p-2 sm:p-3 cursor-pointer hover:border-[#0077b5] transition-colors mb-2">
              <FiPaperclip className="text-lg text-[#0077b5] mb-0.5" />
              <span className="text-xs font-medium text-[#53545a]">
                {uploadFile ? uploadFile.name : 'Upload File'}
              </span>
              <input
                type="file"
                className="hidden"
                onChange={(e) =>
                  setUploadFile(e.target.files?.[0] || null)
                }
              />
            </label>

            <textarea
              value={q.pendingReason || ''}
              onChange={(e) => updateQuestion(q.id, { pendingReason: e.target.value })}
              placeholder="Write the reason here..."
              className="w-full border border-[#919191] rounded-xl p-2 text-xs text-[#53545a] placeholder-[#919191] outline-none focus:border-[#0077b5] resize-none h-16"
            />
          </div>
         )
        */}
            </div>

            <div className="mt-3 ml-6">
                <p className="text-sm font-semibold text-[#F47F2E] mb-1.5">
                    Confidence
                </p>
                <div className="flex flex-wrap gap-3">
                    {confidenceLevels.map((level) => {
                        const isActive =
                            q.pendingConfidence !== null
                                ? q.pendingConfidence === level
                                : q.confidence === level;
                        const isPending = q.pendingConfidence === level;
                        return (
                            <label
                                key={level}
                                className="flex items-center gap-1.5 cursor-pointer text-sm text-[#53545a]"
                            >
                                <input
                                    type="radio"
                                    name={`confidence-${q.id}`}
                                    checked={isActive}
                                    onChange={() =>
                                        handleConfidenceChange(q.id, level)
                                    }
                                    className="accent-[#0077b5]"
                                    disabled={isReviewed}
                                />
                                {level}
                                {isPending && (
                                    <span className="text-[10px] text-[#F47F2E] font-medium ml-0.5">
                                        (pending)
                                    </span>
                                )}
                            </label>
                        );
                    })}
                </div>
            </div>

            <div className="mt-4 ml-6 flex items-center justify-between">
                <div className="flex gap-2">
                    {isReviewed ? (
                        <button
                            onClick={() => handleUndo(q.id)}
                            className="px-4 py-1.5 text-xs font-semibold bg-[#F47F2E] text-white hover:bg-[#F47F2E] transition-colors"
                        >
                            Undo
                        </button>
                    ) : (
                        <>
                            {
                                <>
                                    <button
                                        onClick={() => handleApprove(q.id)}
                                        className="px-4 py-1.5 text-xs font-semibold bg-[#0077b5] text-white hover:bg-[#0077b5]/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        Approve
                                    </button>
                                    <button
                                        onClick={() => handleReject(q.id)}
                                        className="px-4 py-1.5 text-xs font-semibold bg-[#F47F2E] text-white hover:bg-[#F47F2E] transition-colors"
                                    >
                                        Reject
                                    </button>
                                </>
                            }

                            {/* {hasPendingChanges && ( */}
                            {
                                <button
                                    onClick={() => handleSaveAnswer(q.id)}
                                    disabled={q.saving}
                                    className="px-4 py-1.5 text-xs font-semibold bg-[#0077b5] text-white hover:bg-[#0077b5]/90 transition-colors disabled:opacity-50"
                                >
                                    {q.saving ? "Saving..." : "Save"}
                                </button>
                            }
                        </>
                    )}

                    {/* Upload Reference Documents — opens the multi-doc modal */}
                    <button
                        onClick={() => setShowDocsModal(true)}
                        disabled={!q.answerId}
                        title={
                            q.answerId
                                ? "Manage reference documents"
                                : "Save an answer first to attach documents"
                        }
                        className="px-4 py-1.5 text-xs font-semibold border border-[#0077b5] text-[#0077b5] hover:bg-[#0077b5]/10 transition-colors inline-flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <FiPaperclip className="text-sm" />
                        Upload Reference Documents
                        {docCount != null && docCount > 0 && (
                            <span className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-[#0077b5] text-white text-[10px] font-bold">
                                {docCount}
                            </span>
                        )}
                    </button>
                </div>
                {isApproved && (
                    <div className="flex-shrink-0">
                        <div className="w-16 h-16 rounded-full border-4 border-[#0077b5] flex items-center justify-center rotate-[-15deg]">
                            <p className="text-[8px] font-bold text-[#0077b5] uppercase tracking-wide">
                                Approved
                            </p>
                        </div>
                    </div>
                )}
                {isRejected && (
                    <div className="flex-shrink-0">
                        <div className="w-16 h-16 rounded-full border-4 border-[#F47F2E] flex items-center justify-center rotate-[15deg]">
                            <p className="text-[8px] font-bold text-[#F47F2E] uppercase tracking-wide">
                                Rejected
                            </p>
                        </div>
                    </div>
                )}
            </div>

            {/* Explanation Section with smooth animation */}
            <div
                className={`overflow-hidden transition-all duration-300 ease-in-out ${
                    q.showExplanation
                        ? "max-h-[5000px] opacity-100 mt-4"
                        : "max-h-0 opacity-0 mt-0"
                }`}
            >
                <div className="border-t border-[#919191]/20 pt-4">
                    {rec && (
                        <div className="flex items-center justify-between mb-3">
                            <h4 className="text-sm font-bold text-[#53545a]">
                                Review
                            </h4>

                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => setShowDetails(!showDetails)}
                                    className="px-4 py-1.5 text-xs font-semibold bg-[#0077b5] text-white hover:bg-[#0077b5]/90 transition-colors"
                                >
                                    {showDetails
                                        ? "Hide Details"
                                        : "View Full Details →"}
                                </button>
                                <button
                                    onClick={() =>
                                        updateQuestion(q.id, {
                                            showExplanation: !q.showExplanation,
                                        })
                                    }
                                    className="p-1.5 rounded-lg hover:bg-[#919191]/10 text-[#919191] transition-colors"
                                    title="Close explanation"
                                >
                                    <FiX className="text-sm" />
                                </button>
                            </div>
                        </div>
                    )}

                    {rec && (
                        <div className="flex items-start gap-3 bg-[#f0f7ff] border-l-4 border-[#0077b5] px-4 py-3 mb-4">
                            <div>
                                <p className="text-xs text-[#53545a] leading-relaxed">
                                    Review the submitted responses,
                                    explanations, rationale, and supporting
                                    evidence. Validate that the information is
                                    complete, accurate, and sufficient, and add
                                    any reviewer comments, clarifications, or
                                    supporting documents that will enhance the
                                    assessment for its intended consumers.
                                </p>
                            </div>
                        </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="bg-[#ffffff] rounded-xl p-4">
                            {rec && (
                                <div className="flex items-center gap-2 mb-2">
                                    <FiFileText className="text-[#919191]" />
                                    <span className="text-xs font-semibold text-[#919191]">
                                        Rationale & Cross-Walk
                                    </span>
                                </div>
                            )}

                            {rec && (
                                <p className="text-xs text-[#53545a] leading-tight mb-3">
                                    {rec?.rationale}
                                </p>
                            )}

                            {/* Source Info Section - Clean & Simple */}

                            {rec ? (
                                <div className=" bg-white border border-[#e0e2e6] overflow-hidden">
                                    {/* Header */}
                                    <div className="bg-[#0077b5] px-4 py-2">
                                        <p className="text-xs font-semibold text-white text-center">
                                            Source Mapping
                                        </p>
                                    </div>

                                    {/* Content - Simple Table Layout */}
                                    <div className="divide-y divide-[#e0e2e6]">
                                        {/* Assessment Row */}
                                        <div className="flex items-center px-4 py-3 hover:bg-[#f7f8fa] transition-colors">
                                            <div className="w-28 flex-shrink-0">
                                                <span className="text-[11px] font-semibold text-[#919191] uppercase">
                                                    Assessment
                                                </span>
                                            </div>
                                            <div className="flex-1">
                                                <p className="text-sm text-[#53545a] font-medium">
                                                    {sourceAnswer?.find(
                                                        (sa) =>
                                                            sa?.question_id ===
                                                            rec?.source_question_id,
                                                    )?.assessment_description ||
                                                        "—"}
                                                </p>
                                            </div>
                                        </div>

                                        {/* Questionnaire Row */}
                                        <div className="flex items-center px-4 py-3 hover:bg-[#f7f8fa] transition-colors">
                                            <div className="w-28 flex-shrink-0">
                                                <span className="text-[11px] font-semibold text-[#919191] uppercase">
                                                    Questionnaire
                                                </span>
                                            </div>
                                            <div className="flex-1">
                                                <p className="text-sm text-[#53545a] font-medium">
                                                    {sourceAnswer?.find(
                                                        (sa) =>
                                                            sa?.question_id ===
                                                            rec?.source_question_id,
                                                    )?.questionnaire_name ||
                                                        "—"}
                                                </p>
                                            </div>
                                        </div>

                                        {/* Question Row */}
                                        <div className="flex items-start px-4 py-3 hover:bg-[#f7f8fa] transition-colors">
                                            <div className="w-28 flex-shrink-0 pt-0.5">
                                                <span className="text-[11px] font-semibold text-[#919191] uppercase">
                                                    Question
                                                </span>
                                            </div>
                                            <div className="flex-1">
                                                <p className="text-sm text-[#53545a] font-medium leading-relaxed">
                                                    {sourceAnswer?.find(
                                                        (sa) =>
                                                            sa?.question_id ===
                                                            rec?.source_question_id,
                                                    )?.question_text || "—"}
                                                </p>
                                            </div>
                                        </div>

                                        {/* Answer Row - Highlighted */}
                                        <div
                                            className={`flex items-start px-4 py-3 border-l-4 ${isNoMatch ? "bg-[#FFEBEE] border-[#F44336]" : "bg-[#E8F5E9] border-[#4CAF50]"}`}
                                        >
                                            <div className="w-28 flex-shrink-0 pt-0.5">
                                                <span
                                                    className={`text-[11px] font-semibold uppercase ${isNoMatch ? "text-[#C62828]" : "text-[#2E7D32]"}`}
                                                >
                                                    Answer
                                                </span>
                                            </div>
                                            <div className="flex-1">
                                                <p
                                                    className={`text-sm font-semibold leading-relaxed ${isNoMatch ? "text-[#C62828]" : "text-[#2E7D32]"}`}
                                                >
                                                    {sourceAns?.answer_text ||
                                                        "—"}

                                                    <span
                                                        className={`text-xs font-semibold ml-2 ${isNoMatch ? "text-[#F44336]" : "text-[#4CAF50]"}`}
                                                    >
                                                        {isNoMatch
                                                            ? "(No Match — please choose an answer manually from the options & save)"
                                                            : `(${matchPercent}% match)`}
                                                    </span>
                                                </p>
                                            </div>
                                        </div>

                                        {/* Crosswalk Document Row — evidence docs the crosswalk grounded this proposal on */}
                                        {rec?.evidence_documents?.length >
                                            0 && (
                                            <div className="flex items-center px-4 py-3 hover:bg-[#f7f8fa] transition-colors">
                                                <div className="w-28 flex-shrink-0">
                                                    <span className="text-[11px] font-semibold text-[#919191] uppercase">
                                                        Document
                                                    </span>
                                                </div>
                                                <div className="flex-1 flex flex-wrap items-center gap-3">
                                                    {rec.evidence_documents
                                                        .filter(
                                                            (d) => d.blob_url,
                                                        )
                                                        .map((d, i) => (
                                                            <button
                                                                key={
                                                                    d.ref_doc_id ??
                                                                    d.blob_url ??
                                                                    i
                                                                }
                                                                onClick={() =>
                                                                    openEvidenceDoc(
                                                                        d.blob_url,
                                                                    )
                                                                }
                                                                title={`Open document: ${d.doc_title || d.blob_url.split("/").pop()}`}
                                                                className="inline-flex items-center gap-1.5 text-[#0077b5] hover:text-[#005a8f] transition-colors"
                                                            >
                                                                <FiFile className="text-lg flex-shrink-0" />
                                                                <span className="text-xs max-w-[180px] truncate">
                                                                    {d.doc_title ||
                                                                        d.blob_url
                                                                            .split(
                                                                                "/",
                                                                            )
                                                                            .pop()}
                                                                </span>
                                                            </button>
                                                        ))}
                                                </div>
                                            </div>
                                        )}

                                        {/* Evidence Row */}
                                        {(q.additionalInfo ||
                                            q.additionalInfoUrl) && (
                                            <div className="flex items-center px-4 py-3 hover:bg-[#f7f8fa] transition-colors">
                                                <div className="w-28 flex-shrink-0">
                                                    <span className="text-[11px] font-semibold text-[#919191] uppercase">
                                                        Evidence -
                                                    </span>
                                                </div>
                                                <div className="flex-1 flex items-center gap-2">
                                                    {q.additionalInfo && (
                                                        <span className="text-sm text-[#53545a]">
                                                            {q.additionalInfo}
                                                        </span>
                                                    )}
                                                    {q.additionalInfoUrl && (
                                                        <button
                                                            onClick={
                                                                handleOpenDocument
                                                            }
                                                            title={`Open: ${q.additionalInfoUrl.split("/").pop()}`}
                                                            className="text-[#0077b5] hover:text-[#005a8f] transition-colors flex-shrink-0"
                                                        >
                                                            <FiFile className="text-lg" />
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ) : (
                                <div className="flex items-center gap-3 p-3">
                                    <FiAlertCircle
                                        className="text-[#F47F2E] flex-shrink-0"
                                        size={20}
                                    />
                                    <p className="text-sm font-medium text-[#53545a]">
                                        No cross-walk match found. Please select
                                        an answer manually.
                                    </p>
                                </div>
                            )}
                        </div>
                        <div className="bg-[#ffffff] rounded-xl p-4 flex flex-col h-full">
                            <div className="flex items-center gap-2 mb-2 flex-shrink-0">
                                <FiFileText className="text-[#919191]" />
                                <span className="text-xs font-semibold text-[#919191]">
                                    Reviewer Notes
                                </span>
                            </div>
                            <textarea
                                value={q.reviewerNotes}
                                onChange={(e) =>
                                    updateQuestion(q.id, {
                                        reviewerNotes: e.target.value,
                                    })
                                }
                                onBlur={() => saveReviewerNotes(q.id)}
                                placeholder="write a note..."
                                className="w-full flex-1 min-h-[120px] bg-white border border-[#919191] p-2 text-xs text-[#919191] placeholder-[#919191] outline-none focus:border-[#0077b5] resize-none"
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Detailed Review Panel - floats on top of card content on the right */}
            <div
                className={`absolute top-0 right-0 h-full w-full sm:w-[280px] md:w-[320px] bg-white border-l border-[#919191] shadow-lg p-3 sm:p-4 transition-transform duration-300 ease-in-out ${
                    showDetails && q.showExplanation
                        ? "translate-x-0"
                        : "translate-x-full"
                }`}
            >
                <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-bold text-[#53545a]">
                        Detailed Review
                    </h4>
                    <button
                        onClick={() => setShowDetails(false)}
                        className="p-1 rounded-lg hover:bg-[#919191]/10 text-[#919191] transition-colors"
                    >
                        <FiX className="text-sm" />
                    </button>
                </div>
                {/* <p className="text-xs text-[#919191] mb-4 leading-relaxed">{q.detailedReview.description}</p> */}
                <div className="flex flex-wrap gap-1.5 mb-2 sm:mb-3">
                    {["Cross-Walk", "Provenance", "Audit"].map((tab) => (
                        <button
                            key={tab}
                            onClick={() => setDetailTab(tab)}
                            className={`px-2 sm:px-3 py-1 sm:py-1.5 text-[9px] sm:text-[10px] font-semibold transition-colors ${
                                detailTab === tab
                                    ? "bg-[#F47F2E] text-white"
                                    : "bg-[#919191]/10 text-[#919191] hover:bg-[#919191]/20 border border-[#919191]"
                            }`}
                        >
                            {tab}
                        </button>
                    ))}
                </div>
                <div className="text-xs text-[#919191] h-[calc(100%-80px)] overflow-hidden">
                    {detailTab === "Provenance" && (
                        <>
                            <div>
                                <p className="font-semibold text-[#53545a] mb-1">
                                    Provenance Example:
                                </p>
                            </div>
                        </>
                    )}
                    {detailTab === "Cross-Walk" && (
                        <div className="h-full flex flex-col">
                            <p className="font-semibold text-[#53545a] mb-1.5 flex-shrink-0">
                                Cross-Walk Details:
                            </p>
                            <style>{`
              .custom-scrollbar {
                scrollbar-width: thin;
                scrollbar-color: #0077b5 #f7f8fa;
              }
              .custom-scrollbar::-webkit-scrollbar {
                width: 8px;
              }
              .custom-scrollbar::-webkit-scrollbar-track {
                background: #f7f8fa;
                border-radius: 10px;
              }
              .custom-scrollbar::-webkit-scrollbar-thumb {
                background: linear-gradient(180deg, #0077b5 0%, #005a8f 100%);
                border-radius: 10px;
                border: 2px solid #f7f8fa;
              }
              .custom-scrollbar::-webkit-scrollbar-thumb:hover {
                background: linear-gradient(180deg, #005a8f 0%, #00324b 100%);
              }
            `}</style>
                            {rec &&
                                (() => {
                                    const matchingSource = sourceAnswer?.find(
                                        (sa) =>
                                            sa?.question_id ===
                                            rec?.source_question_id,
                                    );

                                    return (
                                        <div className="custom-scrollbar bg-[#f7f8fa] border border-[#e0e2e6] p-2 flex-1 overflow-y-auto min-h-0">
                                            {/* Display structured rationale_details if available */}
                                            {rec?.rationale_details && (
                                                <div className="space-y-2">
                                                    {rec.rationale_details
                                                        .source_evidence && (
                                                        <div className="bg-white p-2 border border-[#e0e2e6]">
                                                            <p className="text-[10px] sm:text-xs font-semibold text-[#0077b5] mb-1">
                                                                Source Evidence:
                                                            </p>
                                                            <p className="text-[10px] sm:text-xs text-[#53545a] leading-relaxed">
                                                                {
                                                                    rec
                                                                        .rationale_details
                                                                        .source_evidence
                                                                }
                                                            </p>
                                                        </div>
                                                    )}

                                                    {rec.rationale_details
                                                        .control_alignment && (
                                                        <div className="bg-white p-2 border border-[#e0e2e6]">
                                                            <p className="text-[10px] sm:text-xs font-semibold text-[#0077b5] mb-1">
                                                                Control
                                                                Objective
                                                                Alignment:
                                                            </p>
                                                            <p className="text-[10px] sm:text-xs text-[#53545a] leading-relaxed">
                                                                {
                                                                    rec
                                                                        .rationale_details
                                                                        .control_alignment
                                                                }
                                                            </p>
                                                        </div>
                                                    )}

                                                    {rec.rationale_details
                                                        .target_need && (
                                                        <div className="bg-white  p-2 border border-[#e0e2e6]">
                                                            <p className="text-[10px] sm:text-xs font-semibold text-[#0077b5] mb-1">
                                                                Target Need:
                                                            </p>
                                                            <p className="text-[10px] sm:text-xs text-[#53545a] leading-relaxed">
                                                                {
                                                                    rec
                                                                        .rationale_details
                                                                        .target_need
                                                                }
                                                            </p>
                                                        </div>
                                                    )}

                                                    {rec.rationale_details
                                                        .org_relevance && (
                                                        <div className="bg-white p-2 border border-[#e0e2e6]">
                                                            <p className="text-[10px] sm:text-xs font-semibold text-[#0077b5] mb-1">
                                                                Organization
                                                                Relevance:
                                                            </p>
                                                            <p className="text-[10px] sm:text-xs text-[#53545a] leading-relaxed">
                                                                {
                                                                    rec
                                                                        .rationale_details
                                                                        .org_relevance
                                                                }
                                                            </p>
                                                        </div>
                                                    )}

                                                    {rec.rationale_details
                                                        .caveats && (
                                                        <div className="bg-white p-2 border border-[#e0e2e6]">
                                                            <p className="text-[10px] sm:text-xs font-semibold text-[#0077b5] mb-1">
                                                                Caveats:
                                                            </p>
                                                            <p className="text-[10px] sm:text-xs text-[#53545a] leading-relaxed">
                                                                {
                                                                    rec
                                                                        .rationale_details
                                                                        .caveats
                                                                }
                                                            </p>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })()}
                        </div>
                    )}
                    {detailTab === "Document Evidence" && (
                        <div className="h-full flex flex-col">
                            <div className="flex items-center justify-between gap-2 mb-2 flex-shrink-0">
                                <p className="font-semibold text-[#53545a] inline-flex items-center gap-1.5">
                                    <FiPaperclip className="text-[#0077b5]" />
                                    Document Evidence
                                </p>
                                {snippetEvidenceDocuments.length > 0 && (
                                    <span className="text-[10px] font-semibold text-[#0077b5] bg-[#0077b5]/10 px-2 py-1">
                                        {snippetEvidenceDocuments.length}{" "}
                                        snippet
                                        {snippetEvidenceDocuments.length === 1
                                            ? ""
                                            : "s"}
                                    </span>
                                )}
                            </div>

                            <div className="custom-scrollbar bg-[#f7f8fa] border border-[#e0e2e6] p-2 flex-1 overflow-y-auto min-h-0 space-y-2">
                                {documentEnrichment && (
                                    <div className="bg-white border border-[#e0e2e6] p-2">
                                        <div className="flex items-start justify-between gap-2 mb-1.5">
                                            <p className="text-[10px] sm:text-xs font-semibold text-[#0077b5]">
                                                Support Summary
                                            </p>
                                            <span className="text-[8px] font-semibold text-[#2E7D32] bg-[#E8F5E9] px-2 py-1">
                                                {formatSupportStatus(
                                                    documentEnrichment.support_status,
                                                )}
                                            </span>
                                        </div>
                                        {Number.isFinite(
                                            Number(
                                                documentEnrichment.evidence_strength,
                                            ),
                                        ) && (
                                            <p className="text-[10px] text-[#F47F2E] mb-1">
                                                Evidence strength:{" "}
                                                {formatEvidenceScore(
                                                    documentEnrichment.evidence_strength,
                                                ) ||
                                                    Number(
                                                        documentEnrichment.evidence_strength,
                                                    ).toFixed(2)}
                                            </p>
                                        )}
                                        {documentEnrichment.enriched_rationale && (
                                            <p className="text-[10px] sm:text-xs text-[#53545a] leading-relaxed">
                                                {
                                                    documentEnrichment.enriched_rationale
                                                }
                                            </p>
                                        )}
                                        {!documentEnrichment.enriched_rationale &&
                                            documentEnrichment.findings && (
                                                <p className="text-[10px] sm:text-xs text-[#53545a] leading-relaxed">
                                                    {
                                                        documentEnrichment.findings
                                                    }
                                                </p>
                                            )}
                                    </div>
                                )}

                                {snippetEvidenceDocuments.length > 0 ? (
                                    snippetEvidenceDocuments.map((d, i) => {
                                        const scoreLabel = formatEvidenceScore(
                                            d.retrieval_score,
                                        );
                                        return (
                                            <div
                                                key={
                                                    d.chunk_id ??
                                                    `${d.blob_url || d.doc_title || "evidence"}-${i}`
                                                }
                                                className="bg-white border border-[#e0e2e6] p-2.5"
                                            >
                                                <div className="flex items-start justify-between gap-2 mb-2">
                                                    <div className="min-w-0">
                                                        <p className="text-[10px] sm:text-xs font-semibold text-[#0077b5] truncate">
                                                            {evidenceTitle(d)}
                                                        </p>
                                                        {(d.section_title ||
                                                            d.chunk_ordinal) && (
                                                            <p className="text-[10px] text-[#919191] mt-0.5">
                                                                {d.section_title ||
                                                                    "Document Section"}
                                                                {d.chunk_ordinal
                                                                    ? ` - Snippet ${d.chunk_ordinal}`
                                                                    : ""}
                                                            </p>
                                                        )}
                                                    </div>
                                                    {scoreLabel && (
                                                        <span className="flex-shrink-0 text-[10px] font-semibold text-[#2E7D32] bg-[#E8F5E9] px-2 py-1">
                                                            {scoreLabel}
                                                        </span>
                                                    )}
                                                </div>
                                                <p className="text-[10px] sm:text-xs text-[#53545a] leading-relaxed">
                                                    {d.snippet}
                                                </p>
                                                {d.blob_url && (
                                                    <button
                                                        onClick={() =>
                                                            openEvidenceDoc(
                                                                d.blob_url,
                                                            )
                                                        }
                                                        className="mt-2 inline-flex items-center gap-1.5 text-[10px] font-semibold text-[#0077b5] hover:text-[#005a8f] transition-colors"
                                                    >
                                                        <FiFile className="text-xs flex-shrink-0" />
                                                        Download Source File
                                                    </button>
                                                )}
                                            </div>
                                        );
                                    })
                                ) : (
                                    <div className="bg-white border border-[#e0e2e6] p-3">
                                        <p className="text-[10px] sm:text-xs font-semibold text-[#53545a] mb-1">
                                            No document snippets returned
                                        </p>
                                        <p className="text-[10px] sm:text-xs text-[#919191] leading-relaxed">
                                            This proposal includes linked
                                            document titles, but the API
                                            response did not contain any
                                            evidence snippets for this question.
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                    {detailTab === "Audit" && (
                        <div>
                            <p className="font-semibold text-[#53545a] mb-1">
                                Audit Trail:
                            </p>
                            <p>{q.detailedReview.audit}</p>
                        </div>
                    )}
                </div>
            </div>

            {/* ─── View Query Chat — slide-in panel (minimized) ─── */}
            <div
                className={`absolute top-0 right-0 h-full w-full sm:w-[340px] md:w-[380px] bg-white border-l border-[#919191] shadow-lg flex flex-col transition-transform duration-300 ease-in-out z-20 ${
                    showChat && !chatMaximized
                        ? "translate-x-0"
                        : "translate-x-full"
                }`}
            >
                {!chatMaximized && chatBody}
            </div>

            {/* ─── View Query Chat — maximized modal ─── */}
            {showChat && chatMaximized && (
                <div
                    className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
                    onClick={closeChat}
                >
                    <div
                        className="bg-white w-full max-w-3xl h-[85vh] shadow-2xl flex flex-col overflow-hidden"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {chatBody}
                    </div>
                </div>
            )}

            {/* Reference documents modal (React equivalent of Blazor ManageAnswerDocuments) */}
            <ManageAnswerDocumentsModal
                isOpen={showDocsModal}
                assessmentId={assessmentId ? parseInt(assessmentId, 10) : null}
                answerId={q.answerId}
                onClose={(count) => {
                    if (typeof count === "number") setDocCount(count);
                    setShowDocsModal(false);
                }}
            />
        </div>
    );
};

export default QuestionCard;
